/**
 * Date utilities for parsing and filtering M/D/YYYY format dates
 * Used by IssuesResolution dashboard for time-based filtering
 */

/**
 * Parse a shopper date to a local-midnight Date.
 * Accepts: "M/D/YYYY", "M-D-YYYY", "YYYY-MM-DD" (ISO), and any of the above
 * with a trailing time component (e.g. "6/19/2026 14:35:00", "2026-06-19T14:35:00Z").
 * Returns null for anything else. Always returns midnight in the local timezone so
 * day-bucketed range filters (Last 30 Days, etc.) compare apples to apples.
 */
export function parseDate(dateStr) {
  if (dateStr instanceof Date) {
    if (isNaN(dateStr)) return null;
    return new Date(dateStr.getFullYear(), dateStr.getMonth(), dateStr.getDate());
  }
  if (!dateStr || typeof dateStr !== 'string') return null;

  // Strip any trailing time/zone component — we bucket by day.
  const datePart = dateStr.trim().split(/[ T]/, 1)[0];

  // ISO: YYYY-MM-DD
  let m = datePart.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const [, year, month, day] = m.map(Number);
    return new Date(year, month - 1, day);
  }

  // US: M/D/YYYY or M-D-YYYY
  m = datePart.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
  if (m) {
    const [, month, day, year] = m.map(Number);
    return new Date(year, month - 1, day);
  }

  return null;
}

/**
 * Get the start of the week (Sunday) for a given date
 * @param {Date} date - Reference date
 * @returns {Date} - Start of week (Sunday at 00:00:00)
 */
export function getStartOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Get the start of the month for a given date
 * @param {Date} date - Reference date
 * @returns {Date} - First day of month at 00:00:00
 */
export function getStartOfMonth(date) {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Subtract months from a date
 * @param {Date} date - Reference date
 * @param {number} months - Number of months to subtract
 * @returns {Date} - Date with months subtracted
 */
export function subMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() - months);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Subtract weeks from a date
 * @param {Date} date - Reference date
 * @param {number} weeks - Number of weeks to subtract
 * @returns {Date} - Date with weeks subtracted
 */
export function subWeeks(date, weeks) {
  const d = new Date(date);
  d.setDate(d.getDate() - (weeks * 7));
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Subtract days from a date
 * @param {Date} date - Reference date
 * @param {number} days - Number of days to subtract
 * @returns {Date} - Date with days subtracted
 */
export function subDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Format a Date object to M/D/YYYY string (matching database format)
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date string
 */
export function formatDate(date) {
  if (!(date instanceof Date) || isNaN(date)) return '';
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}
